import { assign } from "lodash";
import { ChangeDetectionStrategy, Component, Input, signal } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "primeng/dynamicdialog";
import * as FlatModels from "@medsurf/flat-models";
import * as FlatFacades from '@medsurf/flat-facades';
import { ZodSharedValidator } from "@medsurf/flat-validators";
import { ModalControlComponent } from "@medsurf/flat-components";

@Component({
    selector: 'medsurf-slide-edit-keymap-label-form',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './keymap-label-form.component.html',
    styleUrls: ['./keymap-label-form.component.scss'],
    standalone: false
})
export class KeymapLabelFormComponent {
  @Input({required: true}) set keymapLabel(value: FlatModels.KeymapLabelEntityModels.KeymapLabel) {
    this.#keymapLabel = structuredClone(value);
    if (this.#keymapLabel) {
      this.keymapLabelForm.reset(this.#keymapLabel);
    } else {
      throw new FlatModels.ErrorControlModels.MedsurfError(
        'notification.no_entity_found',
        FlatModels.ErrorControlModels.ErrorLevel.enum.error,
        FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
        "APPS.Author.KeymapLabelFormComponent.set_keymapLabel"
      );
    }
    this.#isSubmitted.set(false);
  }
  get keymapLabel() {
    return this.#keymapLabel;
  }
  
  #isSubmitted = signal(false);
  #keymapLabel: FlatModels.KeymapLabelEntityModels.KeymapLabel;
  keymapLabelForm = new FormGroup(
    {
      index: new FormControl(''),
      text: new FormControl(''),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.KeymapLabelEntityModels.KeymapLabelSchema.pick({
          index: true,
          text: true,
        })
      )
    }
  );
  readonly FlatModels = FlatModels;
  
  constructor(public alertControlFacade: FlatFacades.AlertControlFacade,
              public slideAuthorFacade: FlatFacades.SlideAuthorFacade,
              public dialogService: DialogService,
              public translate: TranslateService) {
  }
  
  onSubmit() {
    if (this.#isSubmitted() || this.keymapLabelForm.invalid) {
      return;
    }
    this.#isSubmitted.set(true);
    
    const keymapLabel = assign(this.keymapLabel, this.keymapLabelForm.value);
    this.slideAuthorFacade.requestUpdateKeymapLabel(keymapLabel, 'history.keymap_update_label').subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.keymapLabelForm);
        this.#isSubmitted.set(false);
      },
      next: () => {
        this.#isSubmitted.set(false);
      }
    });
  }
  
  removeKeymapLabel() {
    const dialogRef = this.dialogService.open(ModalControlComponent, {
      header: this.translate.instant('global.label_remove'),
      data: {
        label: 'global.label_remove_confirm',
        inputLabel: 'global.remove',
        inputClass: FlatModels.ButtonDesignModels.ButtonDesignClass.DANGER
      },
      dismissableMask: true,
      width: '50%'
    });
    dialogRef.onClose.subscribe(remove => {
      if (remove) {
        this.slideAuthorFacade.requestRemoveKeymapLabel(this.keymapLabel, 'history.keymap_remove_label').subscribe({
          error: (result) => this.alertControlFacade.requestAddError(result),
        });
      }
    });
  }
}
